import { captureException } from '@sentry/browser';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators/property.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import type { SidebarComponent } from '../overlays/sidebar.component.js';
import { refreshCartSection } from './helpers/render.helper.js';
import { dispatchEvent, listenToEvent } from './types/events.js';

/**
 * Cart Flyout Component.
 *
 * This component handles the display and logic of the cart
 * through the application.
 *
 * The element should be used in a wrapping component that positions it.
 */
export class CartFlyoutComponent extends LitElement {
  @property({ type: String, reflect: true, attribute: 'aria-hidden' })
  _ariaHidden = 'true';

  @property({ type: String, attribute: 'close-button-label' })
  closeButtonLabel = '';

  override connectedCallback() {
    super.connectedCallback();

    const parentOverlay = this.closest<SidebarComponent>('y-sidebar');
    if (!parentOverlay) {
      captureException(new Error('y-cart-flyout must be used within a y-sidebar component.'));
    }

    parentOverlay?.addEventListener('opened', () => {
      this._ariaHidden = 'false';
      dispatchEvent('CART_FLYOUT_SHOWN');
    });

    parentOverlay?.addEventListener('closed', () => {
      this._ariaHidden = 'true';
      dispatchEvent('CART_FLYOUT_HIDDEN');
    });

    listenToEvent('CART_FLYOUT_SHOW', () => {
      parentOverlay?.show();
    });

    listenToEvent('CART_FLYOUT_RERENDER', async () => {
      await refreshCartSection();
    });
  }

  protected handleClose(): void {
    this.dispatchEvent(new Event('close', { bubbles: true }));
  }

  protected renderHeader() {
    return html`
      <header>
        <slot name="header"></slot>

        <button
          class="close-button"
          aria-label="${ifDefined(this.closeButtonLabel)}"
          @click="${() => this.handleClose()}"
          @keyup.enter="${() => this.handleClose()}"
          @keyup.space="${() => this.handleClose()}"
        >
          <slot name="close-button"></slot>
        </button>
      </header>
    `;
  }

  protected renderFooter() {
    return html`
      <footer>
        <slot name="footer"></slot>
      </footer>
    `;
  }

  protected override render() {
    return html`
      ${this.renderHeader()}

      <div style="margin-block: 1rem; flex-grow: 1; overflow: auto">
        <slot></slot>
      </div>

      ${this.renderFooter()}
    `;
  }

  static override get styles() {
    return [
      css`
        :host {
          flex-direction: column;
          width: 100%;
          max-width: 35rem;
          height: 100%;
          margin-left: auto !important;
          padding: 1rem;
          display: flex;
          background-color: var(--background);
          background-image: var(--background-gradient);
          border: 0.25em solid var(--border);
          max-height: 100dvh;
        }

        @media screen and (min-width: 35rem) {
          :host {
            border-top-left-radius: 12px;
            border-bottom-left-radius: 12px;
            height: calc(100dvh - 24px);
            margin-block: 12px !important;
          }
        }

        header {
          border-bottom: 1px solid var(--accent-1);
          justify-content: space-between;
          align-items: center;
          padding-bottom: 0.5rem !important;
          display: flex;
        }

        ::slotted(.cart-title) {
          font-size: 2.75rem !important;
          line-height: 1.5;
        }

        ::slotted(.close-button) {
          width: 1em;
          height: 1em;
          font-size: 24px;
          display: block;
        }

        .close-button {
          border: none;
          background: none;
          cursor: pointer;
        }

        footer {
          border-top: 1px solid var(--accent-1);
          padding-block: 1rem 0;
          padding-inline: 0.5rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
      `,
    ];
  }
}

export function defineCartFlyoutComponent() {
  if (!customElements.get('y-cart-flyout')) {
    customElements.define('y-cart-flyout', CartFlyoutComponent);
  }
}
